import { useState } from 'react'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, string, InferType } from 'yup'
import { Button } from '@heroui/react'
import TextInput from '@/components/inputs/TextInput'
import useRequest from '@/lib/request/useRequest'
import ntimeLogo from '@/assets/images/H-NTime-R.png'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/(auth)/password/reset/')({
  component: PasswordReset,
  beforeLoad: async ({ location, search }) => {
    if (localStorage.getItem('oldNTime') !== 'false') {
      throw redirect({
        to: '/ntimev1/$',
        params: { _splat: location.pathname },
        search,
      })
    }
  },
})

const schema = object({
  email: string().email('Invalid email').required('Email is required'),
})

type FormData = InferType<typeof schema>

function PasswordReset() {
  const { t } = useTranslation("messages");
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { useMutateRequest } = useRequest()

  const form = useForm<FormData>({
    resolver: yupResolver(schema),
  })

  const resetPasswordRequest = useMutateRequest({
    url: 'api-auth/password/reset/',
    method: 'post',
    successMessage: t("success.password_reset_email_sent"),
    errorMessage: t("error.failed_to_send_password_reset_email"),
  })

  const onSubmit = (data: FormData) => {
    resetPasswordRequest.mutate(
      {
        data: { email: data.email },
      },
      {
        onSuccess: () => {
          setIsSubmitted(true)
        },
      },
    )
  }

  return (
    <div className="flex flex-col items-center h-screen w-screen justify-center p-4">
      <div className="w-full max-w-[25rem]">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <img src={ntimeLogo} className="px-8 w-full max-w-md mx-auto" />

              {!isSubmitted ? (
                <>
                  <div className="text-center text-brandGreen-500 mt-4 mb-4">
                    Confirm your email address and reset your password.
                  </div>

                  <TextInput
                    name="email"
                    label="Email address"
                    autoComplete="email"
                  />

                  <Button
                    type="submit"
                    color="primary"
                    variant="solid"
                    isLoading={resetPasswordRequest.isPending}
                    className="mt-2"
                  >
                    Confirm
                  </Button>

                  <Link
                    to="/auth"
                    className="text-brandGreen-500 text-sm text-center mt-2"
                  >
                    Back to Login
                  </Link>
                </>
              ) : (
                <div className="text-center mt-8">
                  <p className="mb-4">
                    Check your email for a password reset link!
                  </p>
                  <Link to="/auth">
                    <Button color="primary" variant="solid">
                      Back to Login
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default PasswordReset
